// Design tokens for Employee Session V2 (spec §1).
// We're using MUI v4/v5 with sx props, not Tailwind — these tokens are
// imported by all the V2 components so the colors stay in one place.

export const TOKENS = {
  slate900: '#0F172A',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',

  amber900: '#78350F',
  amber700: '#B45309',
  amber600: '#D97706',
  amber300: '#FCD34D',
  amber200: '#FDE68A',
  amber100: '#FEF3C7',
  amber50: '#FFFBEB',

  emerald900: '#064E3B',
  emerald700: '#047857',
  emerald600: '#059669',
  emerald500: '#10B981',
  emerald300: '#6EE7B7',
  emerald200: '#A7F3D0',
  emerald100: '#D1FAE5',
  emerald50: '#ECFDF5',

  red900: '#7F1D1D',
  red700: '#B91C1C',
  red600: '#DC2626',
  red300: '#FCA5A5',
  red200: '#FECACA',
  red100: '#FEE2E2',
  red50: '#FEF2F2',

  blue900: '#1E3A8A',
  blue700: '#1D4ED8',
  blue600: '#2563EB',
  blue200: '#BFDBFE',
  blue100: '#DBEAFE',
  blue50: '#EFF6FF',

  violet900: '#4C1D95',
  violet700: '#6D28D9',
  violet600: '#7C3AED',
  violet200: '#DDD6FE',
  violet100: '#EDE9FE',
  violet50: '#F5F3FF',

  pink900: '#831843',
  pink700: '#BE185D',
  pink600: '#DB2777',
  pink200: '#FBCFE8',
  pink100: '#FCE7F3',
  pink50: '#FDF2F8',

  orange700: '#C2410C',
  orange200: '#FED7AA',
  orange50: '#FFF7ED',

  purple700: '#7E22CE',
  purple200: '#E9D5FF',
  purple50: '#FAF5FF',
};

// Color tier for Points (spec §6). Subtle, never overwhelming.
export function pointTier(pts) {
  const n = Number(pts);
  if (n >= 100) return { color: TOKENS.amber700, dot: TOKENS.amber600 };
  if (n >= 40) return { color: TOKENS.blue700, dot: TOKENS.blue600 };
  return { color: TOKENS.slate700, dot: TOKENS.slate400 };
}

// Game-specific theming for Ticket Out (spec §7).
//
// Games come from the `game` + `assignmachinetogame` Mongo collections —
// they're configured per location, not hardcoded in code. So instead of
// matching on known names, we hash the game string to one of the
// palettes below. The hash is deterministic, so the same game name
// always renders with the same color across the whole report (and
// across renders / sessions).
//
// "OTHER" / null / empty falls through to a neutral slate palette so
// machines that haven't been assigned a game type yet still render.
const GAME_PALETTES = [
  { bg: TOKENS.orange50, border: TOKENS.orange200, text: TOKENS.orange700 },
  { bg: TOKENS.blue50, border: TOKENS.blue200, text: TOKENS.blue700 },
  { bg: TOKENS.purple50, border: TOKENS.purple200, text: TOKENS.purple700 },
  { bg: TOKENS.red50, border: TOKENS.red200, text: TOKENS.red700 },
  { bg: TOKENS.emerald50, border: TOKENS.emerald200, text: TOKENS.emerald700 },
  { bg: TOKENS.amber50, border: TOKENS.amber200, text: TOKENS.amber700 },
  { bg: TOKENS.pink50, border: TOKENS.pink200, text: TOKENS.pink700 },
  { bg: TOKENS.violet50, border: TOKENS.violet200, text: TOKENS.violet700 },
];
const NEUTRAL = {
  bg: TOKENS.slate50,
  border: TOKENS.slate200,
  text: TOKENS.slate700,
};

function hashGame(s) {
  // Simple djb2 hash — stable across all JS engines.
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return h;
}

export function ticketGameColor(game) {
  const g = (game || '').toString().toUpperCase().trim();
  if (!g || g === 'OTHER') return NEUTRAL;
  return GAME_PALETTES[hashGame(g) % GAME_PALETTES.length];
}

// Same dynamic-palette strategy as ticketGameColor — bonus names are
// configured per location in the `managebonus` collection, so we hash
// them rather than hardcode.
export function bonusGameColor(name) {
  const g = (name || '').toString().toUpperCase().trim();
  if (!g) return NEUTRAL;
  return GAME_PALETTES[hashGame(g) % GAME_PALETTES.length];
}

export function shortOverColor(val) {
  if (val < 0) return TOKENS.red700;
  if (val > 0) return TOKENS.emerald700;
  return TOKENS.emerald700;
}

export const fmt = (n) => Number(n || 0).toLocaleString('en-US');
export const fmtMoney = (n) => `$${fmt(n)}`;
